/**
 * Clear -> green -> yellow -> red speed ramp (founder ask, 2026-08-27).
 *
 * Shared by the speed trails so a colour means the same thing whether it is
 * under a player or under the ball. What it does NOT share is the scale: a
 * sprinting player and a struck pass differ by roughly 4x, so each entity
 * carries its own range and the ramp only ever sees a 0..1 fraction.
 */

/** Player speeds, m/s, from this game's distribution: p50 1.0, p90 3.4,
 *  p99 5.8, max 9.5. Below MIN is standing or walking — most of a match. */
export const PLAYER_SPEED = { min: 1.0, max: 6.5 }

/**
 * Ball speeds, m/s: p50 2.7, p90 13.2, p99 22.9 — and a max of 269, which is
 * 969 km/h and therefore a tracking artifact, not a shot. The range is set
 * from the 99th percentile and the fraction is clamped, so one bad frame
 * cannot rescale the whole trail to blue.
 */
export const BALL_SPEED = { min: 2.0, max: 25.0 }

export interface SpeedRange { min: number; max: number }

/** 0..1 across a range, clamped. null when the feed carries no speed. */
export function fraction(speed: number | null | undefined, range: SpeedRange): number | null {
  if (speed == null || !Number.isFinite(speed)) return null
  const f = (speed - range.min) / (range.max - range.min)
  return f < 0 ? 0 : f > 1 ? 1 : f
}

/** A ramp stop: the fraction it sits at, then r, g, b. */
type Stop = readonly [number, number, number, number]

/** Stops of the ramp: transparent, green, yellow, red.
 *  `as const` rather than an annotation — it is a fixed list, and saying so
 *  is what makes the two ends below known to exist. */
const STOPS = [
  [0, 90, 200, 90],      // clear (alpha applied separately) -> green
  [0.4, 90, 200, 90],    // green holds through a jog
  [0.7, 245, 210, 60],   // yellow
  [1, 240, 60, 50],      // red
] as const satisfies readonly Stop[]

const FIRST_STOP: Stop = STOPS[0]
// The `??` is unreachable — STOPS is a non-empty literal — but a computed
// last index is not something the checker can resolve, and falling back to the
// first stop degrades to a flat ramp rather than throwing.
const LAST_STOP: Stop = STOPS[STOPS.length - 1] ?? FIRST_STOP

/**
 * Colour for a 0..1 speed fraction as `rgba(...)`.
 *
 * "Clear" is alpha, not a white stop: a white low end would fog the pitch,
 * and the ask was for slow movement to disappear rather than to be pale.
 */
export function rampColor(t: number, maxAlpha = 0.95): string {
  const v = t < 0 ? 0 : t > 1 ? 1 : t
  let lo: Stop = FIRST_STOP
  let hi: Stop = LAST_STOP
  for (let i = 1; i < STOPS.length; i++) {
    const upper = STOPS[i]
    const lower = STOPS[i - 1]
    if (upper === undefined || lower === undefined) continue
    if (v <= upper[0]) {
      lo = lower
      hi = upper
      break
    }
  }
  const span = hi[0] - lo[0]
  const k = span <= 0 ? 0 : (v - lo[0]) / span
  const mix = (a: number, b: number) => Math.round(a + (b - a) * k)
  // Alpha ramps from clear so a standstill leaves no mark at all. The curve is
  // gentle at the bottom so a jog is visible without walking being.
  const alpha = maxAlpha * Math.pow(v, 0.8)
  return `rgba(${mix(lo[1], hi[1])}, ${mix(lo[2], hi[2])}, ${mix(lo[3], hi[3])}, ${alpha.toFixed(3)})`
}
